import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../App";
import { Button } from "./ui/button";
import { PasswordInput } from "./PasswordInput";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { CheckCircle2, Loader2, KeyRound } from "lucide-react";

export default function DemoSetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, email: null, firstName: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/public/demo/set-password/${token}`);
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          setState({ loading: false, error: e.detail || "This link is not valid.", email: null });
          return;
        }
        const d = await res.json();
        setState({ loading: false, error: null, email: d.email, firstName: d.first_name });
      } catch {
        setState({ loading: false, error: "Could not load this link.", email: null });
      }
    })();
  }, [token]);

  const submit = async () => {
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      const res = await fetch(`${API}/public/demo/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || "Could not set password");
      }
      setDone(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="demo-setpw-error">
        <div className="text-center max-w-sm">
          <p className="text-lg font-semibold text-slate-800">Link unavailable</p>
          <p className="text-slate-500 mt-2">{state.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50 flex items-center justify-center px-4" data-testid="demo-setpw-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-amber-100 p-7">
        {done ? (
          <div className="text-center" data-testid="demo-setpw-success">
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto" />
            <h2 className="text-xl font-bold text-slate-900 mt-3">Password set</h2>
            <p className="text-slate-600 mt-2">You can now log in with your email and password.</p>
            <Button onClick={() => navigate("/login")} className="mt-5 w-full bg-amber-500 hover:bg-amber-600" data-testid="demo-setpw-login-btn">
              Go to Login
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-amber-500 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Set your password</h1>
                <p className="text-sm text-slate-500">{state.email}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label>New password</Label>
                <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} data-testid="demo-setpw-password" />
              </div>
              <div>
                <Label>Confirm password</Label>
                <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="demo-setpw-confirm" />
              </div>
              <Button onClick={submit} disabled={saving} className="w-full h-11 bg-amber-500 hover:bg-amber-600 font-semibold" data-testid="demo-setpw-submit-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set Password & Continue"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
